import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Typography,
  Box,
  Stack,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  SignalCellular4Bar as SignalStrongIcon,
  SignalCellularConnectedNoInternet2Bar as SignalWeakIcon,
  SignalCellularConnectedNoInternet0Bar as SignalOffIcon,
  QrCode2 as QrCodeIcon,
  WhatsApp,
  Facebook,
  Instagram,
} from "@mui/icons-material";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";
import formatSerializedId from "../../utils/formatSerializedId";
import { parseISO, format } from "date-fns";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    [theme.breakpoints.up("md")]: {
      minWidth: "800px",
      minHeight: "500px",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "100%",
      height: "100%",
      minHeight: "100%",
      margin: 0,
      borderRadius: 0,
    },
  },
  tableContainer: {
    maxHeight: "400px",
    overflowY: "auto",
    overflowX: "auto",
  },
  statusChip: {
    borderRadius: "16px",
    fontWeight: 500,
    textTransform: "capitalize",
  },
  actionButton: {
    minWidth: "100px",
  },
  channelIcon: {
    width: 20,
    height: 20,
    marginRight: theme.spacing(1),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
  },
  noConnections: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  hideOnMobile: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  mobileCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const CompanyConnectionsModal = ({ open, onClose, company }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalInfo, setConfirmModalInfo] = useState({
    title: "",
    message: "",
    connectionId: "",
  });

  useEffect(() => {
    if (open && company) {
      fetchConnections();
    }
  }, [open, company]);

  const fetchConnections = async () => {
    if (!company) return;
    
    setLoading(true);
    try {
      // Fazendo uma requisição para listar as conexões da empresa específica
      const { data } = await api.get(`/whatsapp/company`, {
        params: { companyId: company.id }
      });
      setConnections(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "CONNECTED":
        return "success";
      case "DISCONNECTED":
      case "TIMEOUT":
      case "PAIRING":
        return "error";
      case "qrcode":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "CONNECTED":
        return <SignalStrongIcon sx={{ color: "success.main" }} />;
      case "DISCONNECTED":
        return <SignalOffIcon sx={{ color: "error.main" }} />;
      case "TIMEOUT":
      case "PAIRING":
        return <SignalWeakIcon sx={{ color: "error.main" }} />;
      case "qrcode":
        return <QrCodeIcon sx={{ color: "warning.main" }} />;
      default:
        return null;
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case "whatsapp":
        return <WhatsApp style={{ color: "#25D366" }} className={classes.channelIcon} />;
      case "facebook":
        return <Facebook style={{ color: "#3b5998" }} className={classes.channelIcon} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c" }} className={classes.channelIcon} />;
      default:
        return null;
    }
  };



  const handleOpenConfirmationModal = (connection) => {
    setConfirmModalInfo({
      title: "Excluir Conexão",
      message: `Tem certeza que deseja excluir a conexão "${connection.name}"? Esta ação não pode ser desfeita.`,
      connectionId: connection.id,
    });
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    try {
      await api.delete(`/whatsapp/${confirmModalInfo.connectionId}`);
      toast.success("Conexão excluída com sucesso!");
      fetchConnections();
    } catch (err) {
      toastError(err);
    }
    setConfirmModalOpen(false);
    setConfirmModalInfo({
      title: "",
      message: "",
      connectionId: "",
    });
  };



  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Conexões da Empresa: {company?.name}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : connections.length === 0 ? (
          <Box className={classes.noConnections}>
            <Typography variant="h6">
              Nenhuma conexão encontrada
            </Typography>
            <Typography variant="body2">
              Esta empresa não possui conexões configuradas
            </Typography>
          </Box>
        ) : (
          fullScreen ? (
            <Stack spacing={2}>
              {connections.map((connection) => (
                <Paper key={connection.id} className={classes.mobileCard} variant="outlined">
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {getChannelIcon(connection.channel)}
                      <Typography variant="subtitle1">{connection.name}</Typography>
                    </Stack>
                    {user?.super && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenConfirmationModal(connection)}
                        sx={{ color: "error.main" }}
                        title="Excluir Conexão"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Stack>
                  <Stack spacing={1} style={{ marginTop: 8 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="textSecondary">Número</Typography>
                      <Typography variant="body2">
                        {connection.number && connection.channel === "whatsapp"
                          ? formatSerializedId(connection.number)
                          : connection.number || "-"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {getStatusIcon(connection.status)}
                      <Chip
                        label={connection.status}
                        size="small"
                        className={classes.statusChip}
                        color={getStatusColor(connection.status)}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="textSecondary">Atualização</Typography>
                      <Typography variant="body2">{format(parseISO(connection.updatedAt), "dd/MM/yy HH:mm")}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {connection.isDefault ? (
                        <Chip label="Padrão" size="small" color="primary" variant="outlined" />
                      ) : (
                        <Typography variant="body2"></Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Paper className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Nome</TableCell>
                  <TableCell align="center">Canal</TableCell>
                  <TableCell align="center">Número</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center" className={classes.hideOnMobile}>Última Atualização</TableCell>
                  <TableCell align="center" className={classes.hideOnMobile}>Padrão</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {connections.map((connection) => (
                  <TableRow key={connection.id}>
                    <TableCell align="center">{connection.name}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" alignItems="center" justifyContent="center">
                        {getChannelIcon(connection.channel)}
                        <Typography variant="body2">
                          {connection.channel === "whatsapp" ? "WhatsApp" : 
                           connection.channel === "facebook" ? "Facebook" : "Instagram"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      {connection.number && connection.channel === "whatsapp"
                        ? formatSerializedId(connection.number)
                        : connection.number || "-"}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
                        {getStatusIcon(connection.status)}
                        <Chip
                          label={connection.status}
                          size="small"
                          className={classes.statusChip}
                          color={getStatusColor(connection.status)}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="center" className={classes.hideOnMobile}>
                      {format(parseISO(connection.updatedAt), "dd/MM/yy HH:mm")}
                    </TableCell>
                    <TableCell align="center" className={classes.hideOnMobile}>
                      {connection.isDefault ? (
                        <Chip
                          label="Padrão"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {/* Apenas super admin pode excluir conexões */}
                      {user?.super && (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenConfirmationModal(connection)}
                          sx={{ color: "error.main" }}
                          title="Excluir Conexão"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          )
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>

      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
    </Dialog>
  );
};

export default CompanyConnectionsModal;
